/**
 * First-run routing-gate decision (T30.2).
 *
 * A pure function so the branch is unit-testable without rendering a router.
 * The gate (FirstRunGate.tsx) calls this with the loaded household + membership
 * state and routes accordingly.
 *
 * Contract:
 *   - "Unconfigured" keys on LOCATION absence — a household with no coordinates
 *     has never completed setup. A household with a location but no selected
 *     stores is valid (radius mode); we never force a store selection (T30.2
 *     Implementation Plan, Details).
 *   - Only a MANAGER is routed into first run — a member of an existing,
 *     already-configured household never sees setup (the join flow, T30.4, owns
 *     the invitee path). A member whose household happens to lack a location is
 *     not the one who should configure it, so they fall through to the app.
 *   - While household/membership data is still loading we return `app` (no
 *     redirect) so the gate never bounces mid-hydration; the gate re-evaluates
 *     when the queries settle.
 */

export interface FirstRunGateInput {
  /** True until both the current-household and membership-list reads settle. */
  loading: boolean;
  /** Current household's latitude, if loaded. `null`/`undefined` = no location. */
  latitude: number | null | undefined;
  /** The signed-in user's role in the CURRENT household, if loaded. */
  currentRole: 'manager' | 'member' | undefined;
  /**
   * True when the manager already skipped/finished first run for this household
   * (the device-local deferral marker, firstRunDeferral.ts). Lets a manager who
   * chose to skip the location step into the app instead of being bounced back
   * into the flow on the next navigation (AC3). A located household never needs
   * this — it short-circuits on the location check above.
   */
  deferred: boolean;
}

export type FirstRunGateDecision = 'first-run' | 'app';

/**
 * Decide whether a freshly signed-in user should be routed into the first-run
 * setup flow. Returns `'first-run'` only for a manager of a household that has
 * no location yet AND has not already deferred setup; everyone else proceeds
 * straight to the app.
 */
export function decideFirstRun(input: FirstRunGateInput): FirstRunGateDecision {
  if (input.loading) return 'app';
  const hasLocation = input.latitude != null;
  if (hasLocation) return 'app';
  if (input.currentRole !== 'manager') return 'app';
  if (input.deferred) return 'app';
  return 'first-run';
}
